import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import ClientType from '../models/ClientType';
import Suivi from '../models/Suivi';
import Utils from '../utils/Utils';
import translator from '../theme/translator.json';
import ClientService from './ClientService';

class PdfExportService {
  // Helper pour formater la date pour les noms de fichiers
  private static formatDateForFileName(date: Date): string {
    return date.toISOString().split('T')[0].replace(/-/g, '_');
  }

  // Helper pour formater une date lisible
  private static formatDate(timestamp: any): string {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('fr-FR');
    } catch (error) {
      return 'N/A';
    }
  }

  // Helper pour traduire le type de client
  private static translateClientType(type: string, language: string): string {
    if (translator.models.enums.clientTypes[type as keyof typeof translator.models.enums.clientTypes]) {
      return Utils.getTranslation(language, translator.models.enums.clientTypes[type as keyof typeof translator.models.enums.clientTypes]);
    }
    return type;
  }

  // Helper pour le texte de priorité
  private static getPriorityText(priority: number): string {
    return `${priority}`;
  }

  static async exportClientToPdf(
    client: ClientType,
    suivis: Suivi[],
    language: string,
    elementId = 'client-summary-content',
  ): Promise<void> {
    try {
      // Récupérer l'élément à exporter
      const element = document.getElementById(elementId);
      if (!element) {
        throw new Error('Élément à exporter non trouvé');
      }

      // Configuration pour html2canvas
      const canvas = await html2canvas(element, {
        scale: 2, // Meilleure qualité
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        height: element.scrollHeight,
        width: element.scrollWidth,
      });

      const imgData = canvas.toDataURL('image/png');

      // Créer le PDF
      // eslint-disable-next-line new-cap
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      // Dimensions A4 en mm
      const pdfWidth = 210;
      const pdfHeight = 297;

      // Calculer les dimensions de l'image
      const imgWidth = pdfWidth - 20; // Marges de 10mm de chaque côté
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 10; // Marge du haut

      // Ajouter l'image au PDF (première page)
      pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
      heightLeft -= (pdfHeight - 20); // Hauteur utilisée sur la première page

      // Ajouter des pages supplémentaires si nécessaire
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight + 10;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
        heightLeft -= (pdfHeight - 20);
      }

      // Nom du fichier
      const fileName = `client_${client.name}_${this.formatDateForFileName(new Date())}.pdf`;

      // Télécharger le PDF
      pdf.save(fileName);
    } catch (error) {
      console.error('Erreur lors de l\'export PDF:', error);
      throw new Error(language === 'fr'
        ? 'Une erreur est survenue lors de l\'export PDF'
        : 'An error occurred during PDF export');
    }
  }

  static async exportClientSummaryToPdf(
    client: ClientType,
    suivis: Suivi[],
    language: string,
    exportSettings?: {
      includeContactInformation?: boolean;
      includeAdditionalInformation?: boolean;
      includeClientDescription?: boolean;
      includeFollowUpHistory?: boolean;
    },
  ): Promise<void> {
    try {
      // 🚀 FETCH les données fraîches du client depuis le serveur
      const freshClient = await ClientService.get(client.ownerId || '', client.id);
      if (!freshClient) {
        throw new Error(language === 'fr' ? 'Client non trouvé' : 'Client not found');
      }

      // Utiliser les données fraîches du serveur
      const clientToExport = freshClient;

      // Valeurs par défaut si exportSettings n'est pas fourni
      const settings = {
        includeContactInformation: exportSettings?.includeContactInformation ?? true,
        includeAdditionalInformation: exportSettings?.includeAdditionalInformation ?? true,
        includeClientDescription: exportSettings?.includeClientDescription ?? true,
        includeFollowUpHistory: exportSettings?.includeFollowUpHistory ?? true,
      };

      // eslint-disable-next-line new-cap
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      let yPosition = 15;
      const leftMargin = 20;
      const rightMargin = 190;
      const lineHeight = 6;
      const sectionSpacing = 12;

      // En-tête avec nom du client
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(50, 50, 50);

      const clientName = clientToExport.name || 'Client Sans Nom';
      const availableWidth = rightMargin - leftMargin;
      const nameLines = pdf.splitTextToSize(clientName, availableWidth);

      pdf.text(nameLines, leftMargin, yPosition);
      // Ajuster la position Y en fonction du nombre de lignes (approx 9mm par ligne pour font size 24)
      yPosition += (nameLines.length * 9);

      // Ligne de séparation
      pdf.setDrawColor(100, 100, 100);
      pdf.setLineWidth(0.5);
      pdf.line(leftMargin, yPosition, rightMargin, yPosition);
      yPosition += sectionSpacing;

      // Section Informations générales
      if (settings.includeContactInformation) {
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(70, 130, 180);
        pdf.text(Utils.getTranslation(language, translator.components.ficheClient.pdfExport.contactInformation), leftMargin, yPosition);
        yPosition += 8;

        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(50, 50, 50);

        const generalInfo = [
          { label: Utils.getTranslation(language, translator.formLabels.courriel), value: clientToExport.email || 'Non renseigne', icon: 'Email:' },
          { label: Utils.getTranslation(language, translator.formLabels.telephone), value: clientToExport.phone || 'Non renseigne', icon: 'Tel:' },
          { label: Utils.getTranslation(language, translator.formLabels.interlocuteur), value: clientToExport.interlocuteur || 'Non renseigne', icon: 'Contact:' },
          { label: Utils.getTranslation(language, translator.components.ficheClient.pdfExport.address), value: clientToExport.address || 'Non renseignee', icon: 'Adresse:' },
        ];

        generalInfo.forEach((info) => {
          if (yPosition > 270) {
            pdf.addPage();
            yPosition = 20;
          }
          pdf.setFont('helvetica', 'bold');
          pdf.text(`${info.icon}`, leftMargin, yPosition);
          pdf.setFont('helvetica', 'normal');
          const valueLines = pdf.splitTextToSize(info.value, 120);
          pdf.text(valueLines, leftMargin + 25, yPosition);
          yPosition += lineHeight * Math.max(1, valueLines.length) + 2;
        });

        yPosition += sectionSpacing;
      }

      // Section Informations supplémentaires
      if (settings.includeAdditionalInformation) {
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(70, 130, 180);
        pdf.text(Utils.getTranslation(language, translator.components.ficheClient.pdfExport.additionalInformation), leftMargin, yPosition);
        yPosition += 8;

        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(50, 50, 50);

        // Type de client
        const typeLabel = Utils.getTranslation(language, translator.general.clientTypeLabel);
        const typeValue = clientToExport.type ? this.translateClientType(clientToExport.type, language) : 'Non defini';
        pdf.setFont('helvetica', 'bold');
        pdf.text(`${typeLabel}`, leftMargin, yPosition);
        pdf.setFont('helvetica', 'normal');
        pdf.text(typeValue, leftMargin + 35, yPosition);
        yPosition += lineHeight + 2;

        // Priorité
        if (clientToExport.priority) {
          const priorityLabel = Utils.getTranslation(language, translator.general.priority);
          const priorityValue = this.getPriorityText(clientToExport.priority);
          pdf.setFont('helvetica', 'bold');
          pdf.text(`${priorityLabel}`, leftMargin, yPosition);

          // Couleur selon la priorité
          if (clientToExport.priority === 1) {
            pdf.setTextColor(200, 50, 50); // Rouge pour haute priorité
          } else if (clientToExport.priority === 2) {
            pdf.setTextColor(200, 150, 50); // Orange pour moyenne priorité
          } else if (clientToExport.priority >= 3) {
            pdf.setTextColor(50, 150, 50); // Vert pour basse priorité (3 ou plus)
          }
          pdf.setFont('helvetica', 'bold');
          pdf.text(priorityValue, leftMargin + 35, yPosition);
          pdf.setTextColor(50, 50, 50); // Retour à la couleur normale
          yPosition += lineHeight + 2;
        }

        // Tags
        if (clientToExport.tags && clientToExport.tags.length > 0) {
          pdf.setFont('helvetica', 'bold');
          pdf.text(`${Utils.getTranslation(language, translator.components.ficheClient.pdfExport.tags)}:`, leftMargin, yPosition);
          pdf.setFont('helvetica', 'normal');
          pdf.text(clientToExport.tags.join(', '), leftMargin + 35, yPosition);
          yPosition += lineHeight + 2;
        } else {
          pdf.setFont('helvetica', 'bold');
          pdf.text(`${Utils.getTranslation(language, translator.components.ficheClient.pdfExport.tags)}:`, leftMargin, yPosition);
          pdf.setFont('helvetica', 'normal');
          pdf.text(Utils.getTranslation(language, translator.components.ficheClient.pdfExport.noTags), leftMargin + 35, yPosition);
          yPosition += lineHeight + 2;
        }

        // Prochain suivi
        if (clientToExport.nextSuivi) {
          const nextSuiviDate = this.formatDate(clientToExport.nextSuivi);
          pdf.setFont('helvetica', 'bold');
          pdf.text(`${Utils.getTranslation(language, translator.components.ficheClient.dateProchainSuivi)}:`, leftMargin, yPosition);
          pdf.setFont('helvetica', 'normal');
          pdf.text(nextSuiviDate, leftMargin + 45, yPosition);
          yPosition += lineHeight + 2;
        } else {
          pdf.setFont('helvetica', 'bold');
          pdf.text(`${Utils.getTranslation(language, translator.components.ficheClient.dateProchainSuivi)}:`, leftMargin, yPosition);
          pdf.setFont('helvetica', 'normal');
          pdf.text(Utils.getTranslation(language, translator.components.ficheClient.pdfExport.noNextFollowUp), leftMargin + 35, yPosition);
          yPosition += lineHeight + 2;
        }

        // Date de création
        if (clientToExport.createdDate) {
          const createdDate = this.formatDate(clientToExport.createdDate);
          pdf.setFont('helvetica', 'bold');
          pdf.text(`${Utils.getTranslation(language, translator.components.ficheClient.creationDate)}:`, leftMargin, yPosition);
          pdf.setFont('helvetica', 'normal');
          pdf.text(createdDate, leftMargin + 35, yPosition);
          yPosition += lineHeight + 2;
        }

        yPosition += sectionSpacing;
      }

      // Section Notes
      if (settings.includeClientDescription) {
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(70, 130, 180);
        pdf.text(Utils.getTranslation(language, translator.components.ficheClient.pdfExport.clientDescription), leftMargin, yPosition);
        yPosition += 8;

        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(50, 50, 50);

        if (clientToExport.notes && clientToExport.notes.trim()) {
          const notesLines = pdf.splitTextToSize(clientToExport.notes, rightMargin - leftMargin);
          notesLines.forEach((line: string) => {
            if (yPosition > 270) {
              pdf.addPage();
              yPosition = 20;
            }
            pdf.text(line, leftMargin, yPosition);
            yPosition += lineHeight;
          });
        } else {
          pdf.text(Utils.getTranslation(language, translator.components.ficheClient.pdfExport.noNotes), leftMargin, yPosition);
          yPosition += lineHeight;
        }
        yPosition += sectionSpacing;
      }

      // Section Suivis
      if (settings.includeFollowUpHistory) {
        if (yPosition > 220) {
          pdf.addPage();
          yPosition = 20;
        }

        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(70, 130, 180);
        pdf.text(Utils.getTranslation(language, translator.components.ficheClient.pdfExport.followUpHistory), leftMargin, yPosition);
        yPosition += 8;

        pdf.setFontSize(10);
        pdf.setTextColor(50, 50, 50);

        if (suivis && suivis.length > 0) {
          suivis.forEach((suivi, index) => {
            if (yPosition > 260) {
              pdf.addPage();
              yPosition = 20;
            }

            // Date du suivi
            pdf.setFont('helvetica', 'bold');
            pdf.text(`${index + 1}. ${this.formatDate(suivi.date)}`, leftMargin, yPosition);
            yPosition += lineHeight;

            // Notes du suivi
            if (suivi.notes) {
              pdf.setFont('helvetica', 'normal');
              const suiviLines = pdf.splitTextToSize(suivi.notes, rightMargin - leftMargin - 10);
              suiviLines.forEach((line: string) => {
                if (yPosition > 270) {
                  pdf.addPage();
                  yPosition = 20;
                }
                pdf.text(line, leftMargin + 5, yPosition);
                yPosition += lineHeight;
              });
            }
            yPosition += 4; // Espacement entre les suivis
          });
        } else {
          pdf.setFont('helvetica', 'normal');
          pdf.text(Utils.getTranslation(language, translator.components.ficheClient.pdfExport.noFollowUps), leftMargin, yPosition);
          yPosition += lineHeight;
        }
      }

      // Pied de page avec date d'export
      const pageCount = (pdf as any).internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i += 1) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(150, 150, 150);
        const exportDate = new Date().toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US');
        pdf.text(`${Utils.getTranslation(language, translator.components.ficheClient.pdfExport.exportedOn)} ${exportDate}`, leftMargin, 285);
        const pageLabel = Utils.getTranslation(language, translator.components.ficheClient.pdfExport.page);
        const ofLabel = Utils.getTranslation(language, translator.components.ficheClient.pdfExport.of);
        const pageText = `${pageLabel} ${i} ${ofLabel} ${pageCount}`;
        pdf.text(pageText, rightMargin - 30, 285);
      }

      // Nom du fichier
      const fileName = `fiche_client_${clientToExport.name?.replace(/[^a-zA-Z0-9]/g, '_')}_${this.formatDateForFileName(new Date())}.pdf`;

      // Télécharger le PDF
      pdf.save(fileName);
    } catch (error) {
      console.error('Erreur lors de l\'export PDF:', error);
      throw new Error(language === 'fr'
        ? 'Une erreur est survenue lors de l\'export PDF'
        : 'An error occurred during PDF export');
    }
  }
}

export default PdfExportService;
